<template>
  <div
    class="search-input-base"
    :class="{
      'search-input-base--focused': isInputFocused,
      'search-input-base--has-value': searchQuery.length > 0,
    }"
  >
    <!-- Search Box -->
    <div class="search-input-base__box">
      <v-icon size="20" class="search-input-base__icon">mdi-magnify</v-icon>
      <input
        ref="searchInputRef"
        v-model="searchQuery"
        type="text"
        class="search-input-base__input"
        :placeholder="placeholder"
        @input="handleSearchInput"
        @keyup.enter="handleEnter"
        @keydown.down.prevent="navigateSuggestion(1)"
        @keydown.up.prevent="navigateSuggestion(-1)"
        @keydown.escape="closeSuggestions"
        @focus="handleSearchFocus"
        @blur="handleSearchBlur"
      />
      <v-progress-circular
        v-if="isLoadingSuggestions"
        indeterminate
        size="16"
        width="2"
        class="search-input-base__spinner"
      />
      <button
        v-else-if="searchQuery"
        class="search-input-base__clear"
        @click="handleClear"
      >
        <v-icon size="18">mdi-close-circle</v-icon>
      </button>
    </div>

    <!-- Suggestions Dropdown -->
    <Transition name="suggestions">
      <div
        v-if="showSuggestions && (suggestions.length > 0 || isLoadingSuggestions)"
        class="search-input-base__suggestions"
      >
        <div
          v-if="isLoadingSuggestions && suggestions.length === 0"
          class="search-input-base__suggestions-loading"
        >
          <v-progress-circular indeterminate size="16" width="2" />
          <span>Searching...</span>
        </div>
        <template v-else>
          <button
            v-for="(suggestion, index) in suggestions"
            :key="suggestion.id"
            class="search-input-base__suggestion"
            :class="{
              'search-input-base__suggestion--active': selectedSuggestionIndex === index,
            }"
            @mousedown.prevent="handleSuggestionSelect(suggestion)"
          >
            <v-icon size="18" class="search-input-base__suggestion-icon">
              {{ getSuggestionIcon(suggestion.type) }}
            </v-icon>
            <div class="search-input-base__suggestion-content">
              <span class="search-input-base__suggestion-text">{{ suggestion.text }}</span>
              <span
                v-if="suggestion.subtext"
                class="search-input-base__suggestion-subtext"
              >
                {{ suggestion.subtext }}
              </span>
            </div>
            <span class="search-input-base__suggestion-type">{{ suggestion.type }}</span>
          </button>
        </template>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { watch } from "vue";
import { useRouter } from "vue-router";
import { useSearchInput, type SearchSuggestion } from "@/composables/useSearchInput";
import type { UserProfile } from "@cc-consulting-nv/ccsdk";

interface Props {
  /** Placeholder text for the input */
  placeholder?: string;
  /** Initial query value */
  modelValue?: string;
  /** Whether to navigate to /search on Enter (default: true for header, false for search page) */
  navigateOnEnter?: boolean;
  /** Minimum query length to trigger suggestions */
  minQueryLength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: "Search posts, songs, videos...",
  modelValue: "",
  navigateOnEnter: true,
  minQueryLength: 3,
});

const emit = defineEmits<{
  /** Emitted when query changes */
  "update:modelValue": [value: string];
  /** Emitted when search should be executed (Enter pressed or suggestion selected) */
  search: [query: string];
  /** Emitted when a user suggestion is selected */
  "select-user": [user: UserProfile];
  /** Emitted when an audio suggestion is selected */
  "select-audio": [audio: { ulid: string; title: string; username: string }];
  /** Emitted when a hashtag suggestion is selected */
  "select-hashtag": [hashtag: { name: string }];
  /** Emitted when the input is cleared */
  clear: [];
}>();

const router = useRouter();

// Use shared search input composable
const {
  searchInputRef,
  searchQuery,
  isInputFocused,
  showSuggestions,
  suggestions,
  isLoadingSuggestions,
  selectedSuggestionIndex,
  handleSearchInput,
  handleSearchFocus,
  handleSearchBlur,
  navigateSuggestion,
  closeSuggestions,
  getSuggestionIcon,
  clearSearch,
  focusInput,
} = useSearchInput({
  minQueryLength: props.minQueryLength,
  navigateOnEnter: props.navigateOnEnter,
  onSearch: (query) => {
    emit("search", query);
  },
});

// Sync with v-model
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== searchQuery.value) {
      searchQuery.value = newValue;
    }
  },
  { immediate: true }
);

watch(searchQuery, (newValue) => {
  emit("update:modelValue", newValue);
});

// Handle Enter key
function handleEnter() {
  if (
    selectedSuggestionIndex.value >= 0 &&
    suggestions.value[selectedSuggestionIndex.value]
  ) {
    handleSuggestionSelect(suggestions.value[selectedSuggestionIndex.value]);
  } else if (searchQuery.value.trim()) {
    closeSuggestions();
    emit("search", searchQuery.value.trim());
    if (props.navigateOnEnter) {
      router.push({ path: "/search", query: { q: searchQuery.value.trim() } });
      searchQuery.value = "";
    }
  }
}

// Handle suggestion selection
function handleSuggestionSelect(suggestion: SearchSuggestion) {
  closeSuggestions();

  if (suggestion.type === "user") {
    const user = suggestion.data as UserProfile;
    emit("select-user", user);
    if (props.navigateOnEnter) {
      searchQuery.value = "";
      router.push(`/profile/${user.username}`);
    }
    return;
  }

  if (suggestion.type === "audio") {
    const audio = suggestion.data as { ulid: string; title: string; username: string };
    emit("select-audio", audio);
    if (props.navigateOnEnter) {
      searchQuery.value = "";
      router.push(`/song/${audio.ulid}`);
    }
    return;
  }

  if (suggestion.type === "hashtag") {
    const hashtag = suggestion.data as { name: string };
    emit("select-hashtag", hashtag);
    searchQuery.value = `#${hashtag.name}`;
    emit("search", searchQuery.value);
    if (props.navigateOnEnter) {
      router.push({ path: "/search", query: { q: searchQuery.value } });
      searchQuery.value = "";
    }
    return;
  }

  // Query type
  searchQuery.value = suggestion.text;
  emit("search", suggestion.text);
  if (props.navigateOnEnter) {
    router.push({ path: "/search", query: { q: suggestion.text } });
    searchQuery.value = "";
  }
}

// Handle clear button
function handleClear() {
  clearSearch();
  emit("clear");
}

// Expose methods for parent components
defineExpose({
  focus: focusInput,
  clear: clearSearch,
  searchInputRef,
});
</script>

<style scoped lang="scss">
.search-input-base {
  position: relative;
  width: 100%;

  &__box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: rgba(var(--v-theme-on-surface), 0.06);
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-radius: 50px;
    transition: all 0.2s ease;

    @media (min-width: 600px) {
      gap: 12px;
      padding: 12px 16px;
    }
  }

  &--focused &__box {
    background: rgba(var(--v-theme-on-surface), 0.1);
    border-color: rgba(245, 158, 11, 0.4);
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }

  &__icon {
    color: rgba(var(--v-theme-on-surface), 0.5);
    flex-shrink: 0;
  }

  &__input {
    flex: 1;
    min-width: 0;
    border: none;
    background: none;
    outline: none;
    font-family: var(--font-body);
    // Use 16px on mobile to prevent iOS Safari auto-zoom on focus
    // (iOS zooms inputs with font-size < 16px, pushing content off-screen)
    font-size: 1rem;
    color: rgb(var(--v-theme-on-surface));

    @media (min-width: 600px) {
      font-size: 0.9375rem;
    }

    &::placeholder {
      color: rgba(var(--v-theme-on-surface), 0.5);
    }
  }

  &__spinner {
    flex-shrink: 0;
  }

  &__clear {
    display: flex;
    padding: 4px;
    border: none;
    background: none;
    color: rgba(var(--v-theme-on-surface), 0.5);
    cursor: pointer;
    border-radius: 50%;
    transition: all 0.2s ease;

    &:hover {
      color: rgba(var(--v-theme-on-surface), 0.7);
      background: rgba(var(--v-theme-on-surface), 0.12);
    }
  }

  // Suggestions dropdown
  &__suggestions {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
    overflow: hidden;
  }

  &__suggestions-loading {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    color: rgba(var(--v-theme-on-surface), 0.5);
    font-size: 0.875rem;
  }

  &__suggestion {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover,
    &--active {
      background: rgba(var(--v-theme-on-surface), 0.06);
    }

    &--active {
      background: rgba(245, 158, 11, 0.1);
    }
  }

  &__suggestion-icon {
    color: rgba(var(--v-theme-on-surface), 0.5);
    flex-shrink: 0;
  }

  &__suggestion-content {
    flex: 1;
    min-width: 0;
  }

  &__suggestion-text {
    display: block;
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgb(var(--v-theme-on-surface));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__suggestion-subtext {
    display: block;
    font-size: 0.8125rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    margin-top: 2px;
  }

  &__suggestion-type {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), 0.4);
    text-transform: capitalize;
    flex-shrink: 0;
  }
}

// Suggestions transition
.suggestions-enter-active,
.suggestions-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.suggestions-enter-from,
.suggestions-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
